import { List } from 'immutable';
//
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import ErrorIcon from '@mui/icons-material/Error';

import { Form, Formik } from 'formik';

import { accountDiscoverySelectors, accountDiscoveryUtils } from 'app-components/flux/account-discovery';
import { accountsActions, accountsSelectors, accountsTypes } from 'app-components/flux/accounts';

import { institutionLoginsActions } from 'app-components/flux/institution-logins';
import { getLogger, tracker } from 'app-components/utils/core';

import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import InteractiveStateView from 'components/InteractiveStateView';

import StdDialogForm from 'components/Dialogs/StdDialogForm';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import ActiveAccountRow from './ActiveAccountRow';
import NewAccountRow from './NewAccountRow';

const log = getLogger('InstitutionAccountsUpdater/index.js');

export const useStyles = makeStyles()((theme) => ({
  alertRoot: {
    backgroundColor: theme.palette.warning.light,
    alignItems: 'center',
  },
  infoIcon: {
    marginLeft: theme.spacing(1),
    marginTop: -5,
  },
}));

function InstitutionAccountsUpdater(props) {
  const { addIgnoredAccounts, disconnect, onCancel, onReset, scope, trackingProperties, fixFiMode, onSubmit } = props;
  const dispatch = useDispatch();

  const accountsById = useSelector(accountsSelectors.getAccountsById);

  const newAccounts = useSelector((state) => accountDiscoverySelectors.getUpsertableNewAccountsData(state, { scope }));
  const activeAccounts = useSelector((state) => accountDiscoverySelectors.getActiveAccountsData(state, { scope }));
  const deadAccounts = useSelector((state) => accountDiscoverySelectors.getDeadAccountsData(state, { scope }));

  const isPolling = useSelector((state) => accountDiscoverySelectors.getIsPolling(state, { scope }));
  const isSubmitting = useSelector((state) => accountDiscoverySelectors.getIsSubmitting(state, { scope }));
  const institutionLogin = useSelector((state) => accountDiscoverySelectors.getInstitutionLogin(state, { scope }));

  const otherAccountsAtSameInstitution = useSelector((state) =>
    accountDiscoverySelectors.getOtherAccountAtSameInstitution(state, { scope }),
  );
  const businessEnabled = useSelector(entitlementsSelectors.getBusinessEnabled);

  const [enableLinkTo, setEnableLinkTo] = useState(
    newAccounts.some((account) => account.linkToAccountId && account.linkToAccountId !== '0') || !businessEnabled,
  );

  const { classes } = useStyles();

  const hasLinkableAccounts = newAccounts.some(
    (account) =>
      account.linkableAccountIdsSection1?.size ||
      account.linkableAccountIdsSection2?.size ||
      account.linkableAccountIdsSection3?.size,
  );

  const initialValues = newAccounts.reduce((accum, account) => {
    const values = accountDiscoveryUtils.getNewAccountFields(account, account.linkToAccountId, accountsById);
    // eslint-disable-next-line no-param-reassign
    accum[account.formFieldName] = {
      enabled: true,
      name: values.name,
      type: values.type,
      ...(businessEnabled && { usageId: values.usageId }),
      linkToId: account.linkToAccountId,
    };
    return accum;
  }, {});

  const handleCancelAndReset = (reset) => {
    tracker.track(!fixFiMode ? tracker.events.addFIComplete : tracker.events.fixFIComplete, {
      ...trackingProperties,
      accounts_added: 0,
      accounts_ignored: 0,
      accounts_linked: 0,
      accounts_changed_name: 0,
      accounts_changed_type: 0,
      accounts_changed_subtype: 0,
      accounts_already_connected: activeAccounts.length,
    });
    if (reset) {
      if (onReset) {
        onReset();
      }
    } else if (onCancel) {
      onCancel();
    }
  };

  const buildAggregatorParams = (discoveredAccount) => ({
    id: discoveredAccount.id,
    cpId: discoveredAccount.cpId,
    cpLoginId: discoveredAccount.cpLoginId,

    channel: discoveredAccount.channel,

    accountName: discoveredAccount.name,
    accountNumberMasked: discoveredAccount.maskedNumber,
  });

  const handleSubmit = (values) => {
    let numberAdded = 0;
    let numberIgnored = 0;
    let numberLinked = 0;
    let numberChangedNames = 0;
    let numberChangedType = 0;
    let numberChangedSubType = 0;

    let numberChecking = 0;
    let numberCredit = 0;
    let numberLoan = 0;
    let numberAsset = 0;
    let numberInvestment = 0;

    let userLinked = 0;
    let qcsLinked = 0;
    let accountsToUpsert = newAccounts.reduce((list, account) => {
      const accountValues = values[account.formFieldName];
      const { enabled, linkToId, usageId } = accountValues;
      const aggregators = List([accountsTypes.mkAccountAggregator(buildAggregatorParams(account.discoveredAccount))]);
      const accountParams = {
        id: linkToId !== '0' ? linkToId : account.discoveredAccount.shadowAccountId,
        shadowId: account.discoveredAccount.shadowAccountId,
        clientId: uuidv4().toUpperCase(),
        institutionLoginId: institutionLogin.id,
        isIgnored: !enabled,

        name: accountValues.name,
        ...(businessEnabled && { usageType: usageId !== 'PERSONAL' ? 'BUSINESS' : 'PERSONAL' }),
        ...(businessEnabled && usageId !== 'PERSONAL' && usageId !== 'BUSINESS' && { businessId: usageId }),
        subType: accountValues.type.qcsSubType,

        disconnect: disconnect ? true : undefined,
      };
      let accountToUpsert = null;
      if (enabled || addIgnoredAccounts) {
        accountToUpsert = accountsTypes.mkUpdateAccount(accountValues.type.qcsType, { ...accountParams, aggregators });
        list.push(accountToUpsert);
      }
      if (enabled) {
        if (linkToId === '0') {
          numberAdded += 1;
          // track which account type is being added
          switch (accountValues.type.qcsType) {
            case 'BANK':
            case 'CASH':
            case 'UNKNOWN':
              numberChecking += 1;
              break;
            case 'CREDIT':
              numberCredit += 1;
              break;
            case 'INVESTMENT':
              numberInvestment += 1;
              break;
            case 'LOAN':
            case 'OTHER_LIABILITY':
              numberLoan += 1;
              break;
            default:
              numberAsset += 1;
              break;
          }
          if (accountToUpsert) {
            numberChangedNames = account.name !== accountToUpsert.name ? 1 : 0;
            numberChangedType += account.type !== accountToUpsert.type ? 1 : 0;
            numberChangedSubType += account.subType !== accountToUpsert.subType ? 1 : 0;
          }
        } else {
          if (linkToId === account.linkToAccountId) {
            // if we suggest auto-link
            qcsLinked += 1;
          } else {
            userLinked += 1;
          }
          numberLinked += 1;
        }
      } else {
        numberIgnored += 1;
      }
      return list;
    }, []);

    const isStripeAccount = institutionLogin?.channel === 'STRIPE' || institutionLogin?.channel === 'STRIPE_TEST';
    const discoverAddAccounts = !isStripeAccount && institutionLogin.cpSetupMode === 'DISCOVER_AND_ADD_ACCOUNTS';

    // If replacing, change aggregator data in active accounts
    if (institutionLogin.cpSetupMode === 'DISCOVER_AND_REPLACE_ACCOUNTS' || discoverAddAccounts) {
      accountsToUpsert = activeAccounts.reduce((list, { account, discoveredAccount }) => {
        const aggregators = List([accountsTypes.mkAccountAggregator(buildAggregatorParams(discoveredAccount))]);
        list.push(
          accountsTypes.mkUpdateAccount(account.type, {
            id: account.id,
            clientId: uuidv4().toUpperCase(),
            aggregators,
          }),
        );
        return list;
      }, accountsToUpsert);
    }

    // Cleanup DEAD Accounts (effective disconnects but still belongs to login)
    accountsToUpsert = deadAccounts.reduce((list, account) => {
      if (!list.find(({ id }) => id === account.id)) {
        list.push(
          accountsTypes.mkUpdateAccount(account.type, {
            id: account.id,
            clientId: uuidv4().toUpperCase(),
            isCpAccountDeleted: true,
          }),
        );
      }
      return list;
    }, accountsToUpsert);

    if (accountsToUpsert?.length) {
      if (
        institutionLogin.id &&
        institutionLogin.id !== '0' &&
        institutionLogin.cpSetupMode !== 'DISCOVER_AND_REPLACE_ACCOUNTS' &&
        !discoverAddAccounts
      ) {
        new Promise((resolve, reject) => {
          dispatch(accountsActions.batchAccounts(accountsToUpsert, { resolve, reject, scope }));
        })
          .then((_response) =>
            dispatch(
              institutionLoginsActions.refreshAccounts(
                { institutionLoginId: institutionLogin.id },
                { context: 'addAccounts', scope },
              ),
            ),
          )
          .catch((error) => log.warn('accounts update error', error));
      } else {
        dispatch(
          institutionLoginsActions.addInstitutionLoginAndAccounts(
            { institutionLogin, accounts: accountsToUpsert },
            { scope },
          ),
        );
      }
      // remember accounts added for billScout filtering
      onSubmit?.(accountsToUpsert?.map((account) => account.clientId));

      // mixpanel tracking
      tracker.track(!fixFiMode ? tracker.events.addFIComplete : tracker.events.fixFIComplete, {
        ...trackingProperties,
        accounts_added: numberAdded,
        accounts_ignored: numberIgnored,
        accounts_linked: numberLinked,
        accounts_changed_name: numberChangedNames,
        accounts_changed_type: numberChangedType,
        accounts_changed_subtype: numberChangedSubType,
        accounts_already_connected: activeAccounts.length,

        numberChecking,
        numberCredit,
        numberLoan,
        numberAsset,
        numberInvestment,

        userLinked,
        qcsLinked,
      });
    }
  };

  const renderForm = (formikBag) => {
    const { handleSubmit: formikHandleSubmit, isValid, submitCount, values } = formikBag;

    // This is to make sure multiple accountNames are not the same and multiple accounts are not being linked to same account
    const stagedNames = [];
    const stagedLinkToIds = [];
    Object.values(values).forEach((value) => {
      if (value.enabled) {
        if (value.name) {
          stagedNames.push(value.name.toLowerCase());
        }
        if (value.linkToId !== '0') {
          stagedLinkToIds.push(value.linkToId);
        }
      }
    });

    return (
      <StdDialogForm component={Form} onSubmit={formikHandleSubmit}>
        {newAccounts.size > 0 && (
          <>
            <StdDialogContent sx={{ maxHeight: 370 }}>
              <Box display={'flex'} alignItems={'center'} component={'span'}>
                <Typography variant='titleRegular'>
                  We found&nbsp;
                  {
                    <Box component='span' fontWeight='fontWeightBold'>
                      {newAccounts.size}
                    </Box>
                  }
                  &nbsp;
                  {`account${newAccounts.size !== 1 ? 's' : ''}`} you can add to Simplifi
                </Typography>
                <InfoTooltipIcon
                  size='medium-no-padding'
                  className={classes.infoIcon}
                  tooltipPlacement='right'
                  tooltipText={'Editing the name or account type here will only change what is in Simplifi.'}
                />
              </Box>
              {!isValid && submitCount > 0 && (
                <Alert classes={{ root: classes.alertRoot }} icon={<ErrorIcon />} severity='error' sx={{ my: 1 }}>
                  <Typography sx={{ color: 'text.primary' }} variant='paragraphRegular'>
                    Check your accounts and fix all errors before continuing.
                  </Typography>
                </Alert>
              )}
              <Box>
                {newAccounts.map((account, index) => (
                  <NewAccountRow
                    account={account}
                    accountsById={accountsById}
                    businessEnabled={businessEnabled}
                    enableLinkTo={enableLinkTo}
                    formik={formikBag}
                    index={index}
                    key={account.formFieldName}
                    stagedLinkToIds={stagedLinkToIds}
                    stagedNames={stagedNames}
                  />
                ))}
              </Box>
              {activeAccounts.size > 0 && (
                <>
                  <Box display={'flex'} mt={2} mb={1} component={'span'}>
                    <Typography variant='titleRegular'>
                      Accounts already connected to Simplifi for this username
                    </Typography>
                    <InfoTooltipIcon
                      size='medium-no-padding'
                      className={classes.infoIcon}
                      tooltipPlacement='right'
                      tooltipText='These accounts have already been connected to Simplifi.'
                    />
                  </Box>
                  <Box>
                    {activeAccounts.map((activeAccount) => (
                      <ActiveAccountRow
                        cpName={activeAccount.discoveredAccount.name}
                        cpNumber={activeAccount.discoveredAccount.maskedNumber}
                        key={activeAccount.account.id}
                        name={activeAccount.account.name}
                        type={activeAccount.account.subType}
                      />
                    ))}
                  </Box>
                </>
              )}
              {otherAccountsAtSameInstitution.size > 0 && (
                <>
                  <Box display={'flex'} mt={2} mb={1} component={'span'}>
                    <Typography variant='titleRegular'>
                      Accounts already connected to Simplifi for same institution
                    </Typography>
                    <InfoTooltipIcon
                      size='medium-no-padding'
                      className={classes.infoIcon}
                      tooltipPlacement='right'
                      tooltipText='These accounts are already connected to institution.'
                    />
                  </Box>
                  <Box>
                    {otherAccountsAtSameInstitution.map((account) => (
                      <ActiveAccountRow
                        cpName={account.aggregators.get(0).accountName}
                        cpNumber={account.aggregators.get(0).accountNumberMasked}
                        key={account.id}
                        name={account.name}
                        type={account.subType}
                      />
                    ))}
                  </Box>
                </>
              )}
            </StdDialogContent>
            <StdDialogActions
              primary={{
                children: 'Add',
                disabled: isSubmitting || !stagedNames.length,
                id: 'acct-upsert-submit',
              }}
              tertiaryNode={
                hasLinkableAccounts && businessEnabled ? (
                  <FormControlLabel
                    control={
                      <Switch checked={enableLinkTo} onChange={(event) => setEnableLinkTo(event.target.checked)} />
                    }
                    label='Link to one or more existing accounts'
                  />
                ) : null
              }
            />
          </>
        )}
        {newAccounts.size === 0 && (
          <InteractiveStateView
            primary=' '
            secondary='You have already added all your accounts at this bank.'
            goText='TRY ANOTHER BANK'
            backText='CANCEL'
            goClick={() => handleCancelAndReset(true)}
            backClick={() => handleCancelAndReset(false)}
          />
        )}
      </StdDialogForm>
    );
  };

  const isBusy = isPolling || isSubmitting;

  return (
    <Box display='flex' flex='1' flexDirection='column'>
      {isBusy && (
        <Box display='flex' flexDirection='column' height='0px' style={{ transform: 'translate(0, 48px)' }}>
          <CircularProgress size={130} style={{ margin: 'auto' }} />
        </Box>
      )}
      <Box display='flex' flex='1' flexDirection='column' style={{ visibility: isBusy ? 'hidden' : null }}>
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validateOnChange validateOnSubmit>
          {renderForm}
        </Formik>
      </Box>
    </Box>
  );
}

export default InstitutionAccountsUpdater;
